// src/components/Navbar.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { usePermissions } from "../context/PermissionContext";
import {
  filterAdminNavOptions,
  filterNavByPermission,
  STUDENT_NAV_OPTIONS,
} from "../utils/adminNavigation";
import { emailsEqual } from "../utils/emailCompare";
import { BRAND } from "../utils/brand";
import ResourcesMenuDialog from "./ResourcesMenuDialog";
import BugReportModal from "./BugReportModal";
import "./Navbar.css";

const STUDENT_NAV_STYLES = {
  Explore: { backgroundColor: "#f2c4c4", borderColor: "#f2c4c4" },
  Resources: { backgroundColor: "#d5d1fd", borderColor: "#d5d1fd" },
  "All Courses": { backgroundColor: "#f2c4c4", borderColor: "#f2c4c4" },
  "My Courses": { backgroundColor: "#f9e1be", borderColor: "#f9e1be" },
  LMS: { backgroundColor: "#bff1db", borderColor: "#bff1db" },
};

const LMS_URL = "https://zainematics.learn360lms.com/";

export default function Navbar({
  studentLanding = false,
  scrolled = false,
}) {
  const { user, loginWithRedirect, isAuthenticated, logout } =
    useAuth0();

  const { permissions, loading: permsLoading } = usePermissions();

  const isSuperAdmin =
    isAuthenticated &&
    emailsEqual(user?.email, process.env.REACT_APP_SUPER_USER_EMAIL);

  const visibleAdminOptions = useMemo(() => {
    if (!isAuthenticated || permsLoading) return [];
    return filterAdminNavOptions(permissions, { isSuperAdmin });
  }, [isAuthenticated, permissions, permsLoading, isSuperAdmin]);

  const visibleStudentOptions = useMemo(() => {
    if (!isAuthenticated || permsLoading) return [];
    return filterNavByPermission(STUDENT_NAV_OPTIONS, permissions, {
      isSuperAdmin,
    });
  }, [isAuthenticated, permissions, permsLoading, isSuperAdmin]);

  const isAdmin = visibleAdminOptions.length > 0;
  const heroOverlay = studentLanding && !scrolled;
  const pastelStyle = (style) => (heroOverlay ? undefined : style);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);

  const studentNavClass = [
    "navbar",
    "navbar-expand-lg",
    "fixed-top",
    studentLanding ? "student-nav" : "app-nav",
    studentLanding && scrolled ? "student-nav--solid" : "",
    heroOverlay ? "student-nav--overlay" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={studentLanding ? "student-nav-spacer" : undefined}
      style={
        studentLanding
          ? { marginBottom: 0 }
          : { marginBottom: "80px" }
      }
    >
      <nav
        className={studentNavClass}
        style={{
          justifyContent: "space-between",
          backgroundColor: studentLanding ? undefined : "#FBFBFD",
          height: studentLanding ? "72px" : "80px",
        }}
      >
        <div className="container-fluid">
          <Link to="/" className="navbar-brand">
            <img
              src={BRAND.logoSrc}
              alt={BRAND.name}
              className={studentLanding ? "student-nav__logo" : undefined}
              style={
                studentLanding
                  ? undefined
                  : {
                      marginLeft: "30px",
                      height: "36px",
                      width: "auto",
                      display: "block",
                    }
              }
            />
          </Link>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarSupportedContent"
            aria-controls="navbarSupportedContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
            style={pastelStyle({
              backgroundColor: "#d5d1fd",
              borderColor: "#d5d1fd",
            })}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div
            className="collapse navbar-collapse"
            id="navbarSupportedContent"
            style={studentLanding ? undefined : { marginRight: "30px" }}
          >
            <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
              {isAuthenticated ? (
                <>
                  {isAdmin ? (
                    <li className="nav-item dropdown">
                      <button
                        className="btn btn-secondary dropdown-toggle mx-2"
                        type="button"
                        id="adminActionsDropdown"
                        data-bs-toggle="dropdown"
                        data-bs-popper-config='{"strategy":"fixed"}'
                        aria-expanded="false"
                        style={pastelStyle({
                          backgroundColor: "#f2c4c4",
                          borderColor: "#f2c4c4",
                        })}
                      >
                        Actions
                      </button>
                      <ul
                        className="dropdown-menu dropdown-menu-end nav-dropdown-menu"
                        aria-labelledby="adminActionsDropdown"
                      >
                        {visibleAdminOptions.map((opt) => (
                          <li key={opt.name}>
                            <Link
                              to={opt.path}
                              className="dropdown-item"
                              style={{ backgroundColor: opt.color }}
                            >
                              <i
                                className={opt.icon}
                                style={{ marginRight: "8px" }}
                              />
                              {opt.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ) : (
                    <>
                      {visibleStudentOptions.map((opt) => {
                        // Temporarily hide All Courses (register) and Explore
                        // (Resources menu button is rendered separately below)
                        if (
                          opt.name === "All Courses" ||
                          opt.name === "Explore"
                        ) {
                          return null;
                        }
                        return (
                          <li className="nav-item" key={opt.name}>
                            <Link to={opt.path}>
                              <button
                                type="button"
                                className="btn btn-primary mx-2"
                                style={pastelStyle(STUDENT_NAV_STYLES[opt.name])}
                              >
                                {opt.name}
                              </button>
                            </Link>
                          </li>
                        );
                      })}
                      {/* Explore (logged in, from STUDENT_NAV_OPTIONS) — kept hidden for now:
                      <li className="nav-item">
                        <Link to="/explore">
                          <button
                            type="button"
                            className="btn btn-primary mx-2"
                            style={pastelStyle(STUDENT_NAV_STYLES.Explore)}
                          >
                            Explore
                          </button>
                        </Link>
                      </li>
                      */}
                      <li className="nav-item">
                        <button
                          type="button"
                          className="btn btn-primary mx-2"
                          style={pastelStyle(STUDENT_NAV_STYLES.Resources)}
                          onClick={() => setResourcesOpen(true)}
                        >
                          Resources
                        </button>
                      </li>
                      {/* Temporarily hidden while logged in:
                      {visibleStudentOptions
                        .filter((opt) => opt.name === "All Courses")
                        .map((opt) => (
                          <li className="nav-item" key={opt.name}>
                            <Link to={opt.path}>
                              <button
                                type="button"
                                className="btn btn-primary mx-2"
                                style={pastelStyle(STUDENT_NAV_STYLES[opt.name])}
                              >
                                {opt.name}
                              </button>
                            </Link>
                          </li>
                        ))}
                      */}
                      <li className="nav-item">
                        <button
                          type="button"
                          className="btn btn-primary mx-2"
                          style={pastelStyle(STUDENT_NAV_STYLES.LMS)}
                          onClick={() =>
                            window.open(LMS_URL, "_blank", "noopener,noreferrer")
                          }
                        >
                          LMS
                        </button>
                      </li>
                    </>
                  )}

                  <li className="nav-item dropdown">
                    <button
                      className="btn btn-primary dropdown-toggle d-flex align-items-center mx-2"
                      type="button"
                      id="dropdownMenuButton"
                      data-bs-toggle="dropdown"
                      data-bs-popper-config='{"strategy":"fixed"}'
                      aria-expanded="false"
                      style={pastelStyle(
                        isAdmin
                          ? {
                              backgroundColor: "#bff1db",
                              borderColor: "#bff1db",
                            }
                          : {
                              backgroundColor: "#f2c4c4",
                              borderColor: "#f2c4c4",
                            },
                      )}
                    >
                      {user?.name || user?.email}
                    </button>
                    <ul
                      className="dropdown-menu dropdown-menu-end nav-dropdown-menu"
                      aria-labelledby="dropdownMenuButton"
                    >
                      <li>
                        <button
                          className="dropdown-item nav-dropdown-menu__logout"
                          type="button"
                          onClick={() =>
                            logout({ returnTo: window.location.origin })
                          }
                        >
                          Log Out
                        </button>
                      </li>
                    </ul>
                  </li>

                  {!isAdmin && (
                    <li className="nav-item">
                      <button
                        type="button"
                        className="btn btn-primary mx-2"
                        aria-label="Report a bug"
                        onClick={() => setBugReportOpen(true)}
                        style={pastelStyle({
                          backgroundColor: "#eeeaf8",
                          borderColor: "#eeeaf8",
                          width: "40px",
                          height: "38px",
                          padding: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        })}
                      >
                        <i
                          className="fa-solid fa-bug"
                          style={{ fontSize: "14px", color: "#403b55" }}
                          aria-hidden="true"
                        />
                      </button>
                    </li>
                  )}
                </>
              ) : (
                <>
                  {/* Explore (logged out) — kept hidden for now:
                  <li className="nav-item">
                    <Link to="/explore">
                      <button
                        type="button"
                        className="btn btn-primary mx-2"
                        style={pastelStyle({
                          backgroundColor: "#d5d1fd",
                          borderColor: "#d5d1fd",
                        })}
                      >
                        Explore
                      </button>
                    </Link>
                  </li>
                  */}
                  <li className="nav-item">
                    <button
                      type="button"
                      className="btn btn-primary mx-2"
                      style={pastelStyle({
                        backgroundColor: "#d5d1fd",
                        borderColor: "#d5d1fd",
                      })}
                      onClick={() => setResourcesOpen(true)}
                    >
                      Resources
                    </button>
                  </li>
                  {/* Temporarily hidden when logged out:
                  <li className="nav-item">
                    <Link to="/registercourse">
                      <button
                        type="button"
                        className="btn btn-primary mx-2"
                        style={pastelStyle({
                          backgroundColor: "#f2c4c4",
                          borderColor: "#f2c4c4",
                        })}
                      >
                        Courses
                      </button>
                    </Link>
                  </li>
                  */}
                  <li className="nav-item">
                    <button
                      type="button"
                      className="btn btn-primary mx-2"
                      style={pastelStyle(STUDENT_NAV_STYLES.LMS)}
                      onClick={() =>
                        window.open(LMS_URL, "_blank", "noopener,noreferrer")
                      }
                    >
                      LMS
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className="btn btn-primary"
                      onClick={() => loginWithRedirect()}
                      style={pastelStyle({
                        backgroundColor: "#f2c4c4",
                        borderColor: "#f2c4c4",
                      })}
                    >
                      Log In
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </nav>
      <ResourcesMenuDialog
        open={resourcesOpen}
        onClose={() => setResourcesOpen(false)}
      />
      {bugReportOpen && (
        <BugReportModal onClose={() => setBugReportOpen(false)} />
      )}
    </div>
  );
}
